import { Box } from '@material-ui/core'
import { ClosableDialog } from 'components/controls/dialogs'
import { SimpleTable } from 'components/controls/tables'
import { ImportFileCsv } from 'components/domain/csv/ImportFileCsv'
import { Button } from 'components/facets'
import { ArticleAttributes } from 'components/facets/ArticleAttributes'
import { useStockStore } from 'lib/hooks/useStockStore'
import { useState } from 'react'
import { CSVLink } from 'react-csv'
import { useTranslation } from 'react-i18next'
import {
  bulkEditFinders,
  CARD_LANGUAGES_WITH_FLAGS,
  CountryEdition,
  countryEditionMap,
  FINISH_TYPES_CONVERSIONS,
  Game,
  getDisplayLocationName,
  getFinishTypeString,
  InventoryArticle,
  STOCK_TYPES,
} from 'shared'

interface CsvBulkEditDialogProps {
  open: boolean
  confirmUpdates: (type: string, stock: InventoryArticle[]) => void
  handleClose: () => void
  startingStock: InventoryArticle[]
  activeGame: Game
}

const mapCsvHeaders = (
  headers: string[],
  finders: Record<string, (x: string) => boolean>
): Record<string, string> => {
  const headerMap: Record<string, string> = {}

  headers.forEach((header) => {
    for (const key in finders) {
      if (finders[key](header)) {
        headerMap[header] = key // Match the found name to the standard name
        break
      }
    }
  })

  return headerMap
}

const applyHeaderMapping = (
  row: Record<string, string | number | undefined>,
  headerMap: Record<string, string>
): Record<string, string | number | undefined> => {
  const mappedRow: Record<string, string | number | undefined> = {}

  Object.keys(row).forEach((key) => {
    const standardKey = headerMap[key] || key // Use the standard name or the original name
    mappedRow[standardKey] = row[key]
  })

  return mappedRow
}

export const CsvBulkEditDialog = ({
  open,
  confirmUpdates,
  handleClose,
  startingStock,
  activeGame,
}: CsvBulkEditDialogProps): JSX.Element => {
  const [importedCsv, setImportedCsv] = useState<
    Array<Record<string, string | number | undefined>>
  >([])
  const [bulkOperation, setBulkOperation] = useState<'decreaseQuantity' | 'setPriceAndQuantity'>(
    'decreaseQuantity'
  )
  const { t } = useTranslation()
  const [modifiedStock, setModifiedStock] = useState<InventoryArticle[]>([])
  const { activeStockType } = useStockStore()
  const [contentToDisplayInDialog, setContentToDisplayInDialog] = useState('box')
  importedCsv

  const convertValueFromBooleanColumn = (value: any) => {
    if (value === 'true' || value === true) return true
    if (value === 'false' || value === false || value === '') return false
    return value
  }

  // An empty or missing cell means the regular edition
  const parseCountryEdition = (value: string | number | undefined): CountryEdition =>
    countryEditionMap.find((entry) => entry.label.toLowerCase() === String(value).toLowerCase())
      ?.value ?? CountryEdition.Regular

  const readImportedCsvAndPrepareDataForChanges = (
    importedCsv: Array<Record<string, string | number | undefined>>
  ) => {
    const updatedStock: InventoryArticle[] = []
    const recordsThatMakesChanges: Array<Record<string, string | number | undefined>> = []

    const headers = Object.keys(importedCsv[0] || {}) // Use first row to obtain headers
    const headerMap = mapCsvHeaders(headers, bulkEditFinders)

    const mappedCsv = importedCsv.map((r) => applyHeaderMapping(r, headerMap))
    mappedCsv.forEach((mappedRow) => {
      // The logic remains unchanged
      const matchingArticle = startingStock.find((article) => {
        if (activeStockType === STOCK_TYPES.SINGLES) {
          const parseFinishType = mappedRow.finishType
            ? FINISH_TYPES_CONVERSIONS.find((entry) => entry.label === mappedRow.finishType)?.label
            : getFinishTypeString(
                convertValueFromBooleanColumn(mappedRow.foil),
                false,
                false,
                convertValueFromBooleanColumn(mappedRow.reverseHolo)
              )

          return (
            article.attributes[3] === Number(mappedRow.mkmId) &&
            article.attributes[1] === mappedRow.condition &&
            CARD_LANGUAGES_WITH_FLAGS.find((language) => language.value === article.attributes[2])
              ?.extendedLabel === mappedRow.language &&
            article.attributes[7] === (convertValueFromBooleanColumn(mappedRow.signed) ?? false) &&
            article.attributes[8] === (convertValueFromBooleanColumn(mappedRow.firstEd) ?? false) &&
            (article.attributes[14] ?? CountryEdition.Regular) ===
              parseCountryEdition(mappedRow.countryEdition) &&
            article.attributes[15] === parseFinishType &&
            article.location === (mappedRow.location ?? '')
          )
        } else {
          return (
            article.attributes[3] === Number(mappedRow.mkmId) &&
            CARD_LANGUAGES_WITH_FLAGS.find((language) => language.value === article.attributes[2])
              ?.extendedLabel === mappedRow.language &&
            article.location === (mappedRow.location ?? '')
          )
        }
      })

      if (matchingArticle) {
        if (bulkOperation === 'decreaseQuantity') {
          if (Number(mappedRow.quantity) < 0) {
            matchingArticle!.locations![0].quantity += Number(mappedRow.quantity)
            matchingArticle!.locations![0].deltaQuantity! = Number(mappedRow.quantity)
            updatedStock.push(matchingArticle)

            recordsThatMakesChanges.push(mappedRow)
          }
        } else {
          if (matchingArticle.price !== Number(mappedRow.price)) {
            matchingArticle.newPrice = Number(mappedRow.price)
          }
          if (
            matchingArticle.locations![0].quantity !== Number(mappedRow.quantity) &&
            Number(mappedRow.quantity) > -1
          ) {
            matchingArticle!.locations![0].deltaQuantity! =
              Number(mappedRow.quantity) - matchingArticle.locations![0].quantity
            matchingArticle.locations![0].quantity += matchingArticle!.locations![0].deltaQuantity
          }

          updatedStock.push(matchingArticle)

          recordsThatMakesChanges.push(mappedRow)
        }
      }
    })

    setModifiedStock(updatedStock)

    setImportedCsv(recordsThatMakesChanges)
    setContentToDisplayInDialog('content')
  }

  type ResultDataItem = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any
  }

  const getContentAccordingStep = (step: string) => {
    switch (step) {
      case 'box':
        return (
          <div className="p-3">
            <h3>{t('stockPricing.bulkEdit.csvBulkEdits')}</h3>
            <p>
              {t('stockPricing.bulkEdit.csvBulkEditExplainChannels')}
              <br />
              {t('stockPricing.bulkEdit.csvBulkEditExplainLocation')}
            </p>
            <p className="mt-3">
              {t('stockPricing.bulkEdit.csvBulkEditColumnsRequired')}
              <br />
              {t('stockPricing.bulkEdit.csvBulkEditiSuggestion')}
              <CSVLink
                filename={`example-import.csv`}
                data={activeGame.exampleCsv}
                className="ms-3"
                target="_blank"
              >
                {t('csv.getExampleCsv')}
              </CSVLink>
            </p>
            <div className="text-center w-100 d-flex flex-row justify-content-center">
              <div className="w-75 d-flex flex-row justify-content-between mt-5 ">
                <div className="bulk-edit-box-container d-flex flex-column justify-content-between">
                  <div>
                    <h4 className="primary-color fw-medium">
                      {t('stockPricing.bulkEdit.setPriceAndQuantityBtn')}
                    </h4>
                    <p>{t('stockPricing.bulkEdit.setPriceAndQuantity')}</p>
                  </div>
                  <Button
                    variant="contained"
                    data-testid="set-price-and-quantity"
                    onClick={() => {
                      setBulkOperation('setPriceAndQuantity')
                      setContentToDisplayInDialog('import')
                    }}
                  >
                    {t('stockPricing.bulkEdit.setPriceAndQuantityBtn')}
                  </Button>
                </div>
                <div className="bulk-edit-box-container d-flex flex-column justify-content-between">
                  <div>
                    <h4 className="primary-color fw-medium">
                      {t('stockPricing.bulkEdit.decreaseQuantityBtn')}
                    </h4>
                    <p>{t('stockPricing.bulkEdit.decreaseQuantity')}</p>
                  </div>
                  <Button
                    variant="contained"
                    data-testid="decrease-quantity"
                    onClick={() => {
                      setBulkOperation('decreaseQuantity')
                      setContentToDisplayInDialog('import')
                    }}
                  >
                    {t('stockPricing.bulkEdit.decreaseQuantityBtn')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )
      case 'import': {
        return (
          <div className="p-4">
            <ImportFileCsv
              bulkActions={true}
              readCsvData={readImportedCsvAndPrepareDataForChanges}
              stockType={activeStockType}
              handleResult={() => console.log('handle result')}
            />
          </div>
        )
      }
      case 'content':
        return (
          <div className="p-3">
            <h3 className="mb-4">Import Summary</h3>
            <SimpleTable<ResultDataItem>
              maxWidth="100%"
              options={{ rowStyle: { height: '30px' }, search: false, paging: false }}
              columns={[
                {
                  title: 'Name',
                  sorting: false,
                  render: (article) => <Box width="150px">{article.card.name}</Box>,
                },
                {
                  title: 'Cn',
                  sorting: false,
                  render: (article) => <Box width="70px">{article.card.cn}</Box>,
                },
                {
                  title: 'Set',
                  sorting: false,
                  render: (article) => (
                    <Box width="150px">
                      {article.card.set} - {article.card.expansion}
                    </Box>
                  ),
                },
                {
                  title: 'Delta Q',
                  sorting: false,
                  render: (article) => (
                    <Box textAlign={'center'} width="150px">
                      {article.locations[0].deltaQuantity ? (
                        <span>{article.locations[0].deltaQuantity}</span>
                      ) : (
                        <span className="fst-italic">No changes</span>
                      )}
                    </Box>
                  ),
                },
                {
                  title: 'Location',
                  sorting: false,
                  render: (article) => (
                    <Box textAlign={'center'} width="150px">
                      {getDisplayLocationName(article.location!)}
                    </Box>
                  ),
                },
                {
                  title: 'Price',
                  sorting: false,
                  render: (article) => (
                    <Box textAlign={'center'} width="150px">
                      {article.newPrice ? (
                        <span>
                          {article.attributes[4]} <i className="fa-regular fa-arrow-right px-2"></i>{' '}
                          {article.newPrice}
                        </span>
                      ) : (
                        <div>
                          <span>{article.attributes[4]}</span>{' '}
                          <span className="fst-italic">[No changes]</span>
                        </div>
                      )}
                    </Box>
                  ),
                },
                {
                  title: 'Language',
                  sorting: false,
                  render: (article) => (
                    <Box textAlign={'center'} width="150px">
                      {
                        CARD_LANGUAGES_WITH_FLAGS.find(
                          (language) => language.value === article.attributes[2]
                        )?.extendedLabel
                      }
                    </Box>
                  ),
                },
                {
                  title: 'Properties',
                  sorting: false,
                  field: '',
                  render: (article) => (
                    <div className="d-flex flex-row align-items-center" style={{ width: '120px' }}>
                      <ArticleAttributes iconHeight={19} article={article} />
                    </div>
                  ),
                },
              ]}
              data={modifiedStock.map((article) => article) as ResultDataItem[]}
              title={'Articles that will be modified:'}
            />
            <Button
              variant="contained"
              data-testid="confirm-modifications"
              onClick={() => {
                setContentToDisplayInDialog('box')
                setImportedCsv([])
                handleClose()
                confirmUpdates(bulkOperation, modifiedStock)
              }}
              style={{ marginTop: '24px' }}
            >
              Confirm
            </Button>
          </div>
        )
    }
  }
  return (
    <ClosableDialog
      onClose={() => {
        handleClose()
        setContentToDisplayInDialog('box')
        setImportedCsv([])
      }}
      open={open}
      maxWidth="xl"
    >
      {getContentAccordingStep(contentToDisplayInDialog)}
    </ClosableDialog>
  )
}
